import {createMultiStyleConfigHelpers} from '@chakra-ui/styled-system';

const helpers = createMultiStyleConfigHelpers(['root', 'container', 'itemWrapper', 'closeButton']);

/**
 * Hex colour validator (KL-980). Contentful stores `backgroundColor` / `textColor`
 * as free-text hex strings validated by the same regex on the content model, so
 * they can be applied directly. This guard protects rendering during the content
 * transition (legacy entries may still hold a non-hex enum label until re-saved)
 * and against any invalid value slipping through.
 *
 * @param {string} value - Candidate colour value
 * @returns {boolean} True when `value` is a valid 3- or 6-digit hex colour
 */
const isValidHexColor = (value) => typeof value === 'string' && /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(value.trim());

/**
 * Safe fallbacks used only when the configured value is missing/invalid.
 */
const DEFAULT_BACKGROUND_COLOR = 'greyscale.100'; // #1B1A18
const DEFAULT_TEXT_COLOR = 'white';

/**
 * Fixed height for promo banner to prevent layout shift during animation
 */
const BANNER_HEIGHT = '40px';

/**
 * @typedef {import('@chakra-ui/styled-system').PartsStyleInterpolation<{
 *  keys: ("root" | "container" | "itemWrapper" | "closeButton")[]
 * }>} PromoBannerStyles
 */

export const promoBannerStyles = helpers.defineMultiStyleConfig({
  /**
   * Base styles for PromoBanner component
   *
   * @param {Object} args - Component arguments
   * @param {Contentful.PromoBanner} args.component - PromoBanner component data
   * @param {string} [args.backgroundColor] - Background hex colour from Contentful
   * @param {string} [args.textColor] - Text hex colour from Contentful (parent level)
   *
   * @returns {PromoBannerStyles} - Component styles
   */
  baseStyle({component, backgroundColor, textColor}) {
    // KL-980 — hex values are applied directly (no enum→token map). `color` is set
    // on the root so it cascades to every child item's text and links.
    const bgColor = isValidHexColor(backgroundColor) ? backgroundColor : DEFAULT_BACKGROUND_COLOR;
    const color = isValidHexColor(textColor) ? textColor : DEFAULT_TEXT_COLOR;

    return {
      root: {
        width: '100%',
        backgroundColor: bgColor,
        color,
        py: 2,
        px: 4,
        position: 'relative',
        zIndex: 'banner',
        height: BANNER_HEIGHT,
        overflow: 'hidden',
      },
      container: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        maxWidth: 'container.xxxl',
        mx: 'auto',
        height: '100%',
        overflow: 'hidden',
        position: 'relative',
      },
      itemWrapper: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '100%',
      },
      closeButton: {
        position: 'absolute',
        right: 4,
        top: '50%',
        transform: 'translateY(-50%)',
        bg: 'transparent',
        border: 'none',
        minW: 'auto',
        h: 'auto',
        p: 1,
        _hover: {
          bg: 'transparent',
          opacity: 0.7,
        },
        _active: {
          bg: 'transparent',
        },
        _focus: {
          boxShadow: 'none',
        },
      },
    };
  },

  variants: {
    default: {},
  },
});
